'use client';

import * as React from 'react';

import { useQuery } from '@tanstack/react-query';

import { queryFunctions } from '@hub/common/src/query-functions';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';
import { useCitThemeClient } from '@hub/common-components/src/CitThemeProvider';

import FolderSidebar from '@hub/common-components/src/FolderSidebar';
import ScrolledEntriesDisplay from '@hub/common-components/src/ScrolledEntriesDisplay';
import IFrameNavbar from '@/components/IFrameNavbar';
import LoadingSkeleton from '@hub/common-components/src/LoadingSkeleton';
import ChangeBodyBackgroundColor from '@hub/common-components/src/ChangeBodyBackgroundColor';

interface IFrameAccountProps {
  accountId: string;
  searchQuery: string | null;
  folderId?: string;
}

function SidebarToggleButton({ onClick, isOpen }: { onClick: () => void; isOpen: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`top-[0px] left-0 z-100 w-[30px] h-[30px] rounded-full bg-white shadow-md sidebar-toggle-iframe ${
        isOpen ? 'open' : ''
      }`}
      aria-label='Toggle sidebar'
    >
      {isOpen ? '✕' : '☰'}
    </button>
  );
}

export default function IFrameAccount({ accountId, searchQuery, folderId }: IFrameAccountProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const citOrigin = useCitOriginClient();
  const { accountTheme } = useCitThemeClient();

  const { data: accountDetails, isLoading: isAccountDetailsLoading } = useQuery({
    queryKey: ['account-details', { accountId, citOrigin }],
    queryFn: queryFunctions.getAccountDetails
  });

  const { data: folderDetails, isLoading: isFolderDetailsLoading } = useQuery({
    queryKey: ['folder-details', { accountId, folderId, citOrigin }],
    queryFn: queryFunctions.getFolderDetails
  });

  const handleOverlayClick = () => {
    setIsSidebarOpen(false);
  };

  if (isAccountDetailsLoading || isFolderDetailsLoading || !accountDetails || !folderDetails) {
    // skeleton while loading
    return <LoadingSkeleton />;
  }

  const bgColor = accountTheme.backgroundColor(undefined, true);
  const fgColor = accountTheme.bodyFontColor();

  return (
    <>
      <ChangeBodyBackgroundColor color={bgColor} />
      {/* Background color wrapper */}
      <div className='bg-color-wrapper' style={{ backgroundColor: bgColor }} />

      {/* Toggle button */}
      <SidebarToggleButton
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        isOpen={isSidebarOpen}
      />

      {/* Fixed header */}
      <div className='header-container' style={{ backgroundColor: bgColor }}>
        <div className='flex items-center'>
          <IFrameNavbar bgColor={bgColor} />
        </div>
      </div>

      {/* Overlay */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'show' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* Fixed sidebar */}
      <div className={`sidebar-container-iframe ${isSidebarOpen ? 'open' : ''}`}>
        <div className='h-full overflow-y-auto'>
          <FolderSidebar
            accountId={accountId}
            folders={accountDetails.folders || []}
            selectedFolderId={folderId}
          />
        </div>
      </div>

      {/* Main content */}
      <div className='main-content-wrapper' style={{ backgroundColor: bgColor, color: fgColor }}>
        <div className='mt-[70px] p-4 mb-[70px]'>
          <h1 className='text-xl md:text-2xl font-bold mb-5 md:mb-3 line-clamp-1 break-all pl-10 lg:pl-0'>
            {folderDetails.id
              ? `${folderDetails.name} - ${accountTheme.accountName()}`
              : `All Entries - ${accountTheme.accountName()}`}
          </h1>
          {folderDetails.description ? (
            <div
              className={`text-sm mb-3 prose prose-lg marker:text-inherit max-w-none`}
              style={{ color: `${fgColor}99` }}
              dangerouslySetInnerHTML={{ __html: folderDetails.description }}
            />
          ) : null}
          <ScrolledEntriesDisplay
            accountId={accountId}
            folderId={folderId}
            searchQuery={searchQuery}
          />
        </div>
      </div>
    </>
  );
}
